import React, { ReactNode, createContext } from 'react';

import {
  FitnessApplicationActivityType,
  MeasurementSystem,
  PageTotal,
  PageTotalsDisplayOptions,
} from '../../../types/__generated__';
import { useBasePageQuery } from '../../hooks';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { ModalType } from './FitnessManager';
import {
  FitnessApplicationName,
  useFitnessConnectionSettings,
} from './useFitnessConnectionSettings';

interface FitnessManagerContext {
  status: {
    isLoading: boolean;
    isConnected: boolean;
    hasSettings: boolean;
    isPaused: boolean;
  };
  fitnessApplicationName: FitnessApplicationName;
  page: {
    id: string;
    legacyId: string;
    title: string;
  };
  settings: {
    showMap: boolean;
    measurementSystem: MeasurementSystem;
    subscribedActivities: FitnessApplicationActivityType[];
    showPhotos: boolean;
    showDuration: boolean;
    showDistance: boolean;
    showElevation: boolean;
    showTotaliser: boolean;
  };
  totals: {
    timeline: PageTotal[];
    primaryMeasurementType: string;
  };
  displayOptions?: PageTotalsDisplayOptions;
  modalManager: {
    status: {
      hasCookieConsent: boolean;
    };
    actions: {
      onOpenModal: (modalType: ModalType) => void;
      onCloseModal: () => void;
    };
    modal: ReactNode;
  };
}

export const FitnessManagerCtx = createContext<FitnessManagerContext | null>(
  null,
);

export const useFitnessManagerContext = () =>
  useNotNullableContext(FitnessManagerCtx);

type FitnessManagerContextProps = Pick<
  FitnessManagerContext,
  'modalManager'
> & { children: ReactNode; fitnessApplicationName: FitnessApplicationName };

export function FitnessManagerContext({
  children,
  fitnessApplicationName,
  modalManager,
}: FitnessManagerContextProps) {
  const {
    status,
    settings,
    displayOptions,
    totals,
  } = useFitnessConnectionSettings(fitnessApplicationName);

  const { data } = usePage();

  if (!settings || !status || !totals || !data) return null;

  return (
    <FitnessManagerCtx.Provider
      value={{
        status,
        settings,
        displayOptions,
        totals,
        page: data,
        fitnessApplicationName,
        modalManager,
      }}
    >
      {children}
    </FitnessManagerCtx.Provider>
  );
}

function usePage() {
  const { data: { page } = {} } = useBasePageQuery();

  if (!page) return { data: null };

  return {
    data: {
      id: page.id,
      legacyId: page.legacyId,
      title: page.title,
    },
  };
}
